import { IssueTypeDataT, IssueType } from '@/routes/settings/issueTypes/types/issueType.ts'
import { UniqueIdentifier } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'
import { create } from 'zustand'
type IssueTypeStore = {
  issueTypesList: IssueType | null
  setIssueTypesList: (issueTypesList: IssueType | null) => void
  issueTypeIds: number[]
  highestPriority: number
  editIssueType: IssueTypeDataT | null
  setEditIssueType: (editIssueType: IssueTypeDataT | null) => void
  updateEditIssueType: (updatedFields: Partial<IssueTypeDataT>) => void
  addListElement: (
    listProperty: 'availableProperties' | 'availableListViews',
    element: string,
  ) => void
  removeListElement: (
    listProperty: 'availableProperties' | 'availableListViews',
    element: string,
  ) => void
  updateListOrder: (
    listProperty: 'availableProperties' | 'availableListViews',
    activeId: UniqueIdentifier,
    overId: UniqueIdentifier,
  ) => void
}

const findHighestPriority = (issueTypes: IssueType['hydra:member']): number => {
  if (issueTypes.length === 0) {
    return 0
  }

  const highestStatus = issueTypes.reduce((highest, current) => {
    if (highest.priority === undefined || highest.priority === null) return current
    if (current.priority === undefined || current.priority === null) return highest
    return current.priority > highest.priority ? current : highest
  })

  return highestStatus.priority || 0
}

const useIssueTypeStore = create<IssueTypeStore>(set => ({
  issueTypesList: null,
  setIssueTypesList: issueTypesList => {
    set({
      issueTypesList,
      issueTypeIds:
        issueTypesList?.['hydra:member']
          ?.map(status => status.id)
          .filter((id): id is number => id !== undefined) || [],
      highestPriority: findHighestPriority(issueTypesList?.['hydra:member'] || []),
    })
  },
  issueTypeIds: [],
  highestPriority: 0,
  editIssueType: null,
  setEditIssueType: editIssueType => set({ editIssueType }),
  updateEditIssueType: updatedFields =>
    set(state => {
      // console.log(`updateEditIssueType 👋`, updatedFields)
      return {
        editIssueType: state.editIssueType ? { ...state.editIssueType, ...updatedFields } : null,
      }
    }),
  addListElement: (listProperty, element) => {
    set(state => {
      if (state.editIssueType && state.editIssueType[listProperty]) {
        // only one status group can be selected
        let statusGroupIndex
        let updatedProperties
        if (element.includes('/status_groups')) {
          statusGroupIndex = state.editIssueType[listProperty].findIndex(property =>
            property.includes('/status_groups'),
          )
        }
        if (statusGroupIndex !== undefined && statusGroupIndex !== -1) {
          updatedProperties = state.editIssueType[listProperty]
          updatedProperties.splice(statusGroupIndex, 1, element)
        } else {
          updatedProperties = [...state.editIssueType[listProperty], element]
        }
        return {
          editIssueType: {
            ...state.editIssueType,
            [listProperty]: updatedProperties,
          },
        }
      }
      return state
    })
  },
  removeListElement: (listProperty, element) => {
    set(state => {
      if (state.editIssueType && state.editIssueType[listProperty]) {
        return {
          editIssueType: {
            ...state.editIssueType,
            [listProperty]: state.editIssueType[listProperty].filter(e => e !== element),
          },
        }
      }
      return state
    })
  },
  updateListOrder: (listProperty, activeId, overId) => {
    set(state => {
      if (state.editIssueType && state.editIssueType[listProperty]) {
        const oldIndex = state.editIssueType[listProperty]?.findIndex(value => value === activeId)
        const newIndex = state.editIssueType[listProperty]?.findIndex(value => value === overId)
        const updatedList = arrayMove([...state.editIssueType[listProperty]], oldIndex, newIndex)
        return {
          editIssueType: {
            ...state.editIssueType,
            [listProperty]: updatedList,
          },
        }
      }
      return state
    })
  },
}))

export default useIssueTypeStore
